<template>
  <BaseDialog
    ref="dialogRef"
    :visible="Boolean(dialog)"
    :actions="false"
    :data-testId="dialog?.id"
    closeOnClickOutside
    class="dnl:p-8! dnl:flex dnl:flex-col dnl:gap-6 dnl:max-w-lg!"
    @cancel="close"
  >
    <template
      v-if="dialog?.text"
      #header
    >
      <div class="dnl:font-bold dnl:text-xl">
        {{ $t(dialog?.text) }}
      </div>
    </template>
    <template #content>
      <div class="dnl:flex dnl:items-center dnl:gap-2">
        <button
          :class="[btnClass, discardBtn.className]"
          @click="discard"
        >
          {{ $t(discardBtn.text) }}
        </button>

        <button
          :class="[btnClass, confirmBtn.className]"
          @click="confirm"
        >
          {{ $t(confirmBtn.text) }}
        </button>
      </div>

      <LoadingOverlay v-if="loading" />
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import coreState from '@/store/modules/global/global-state';
  import { clearDialog } from '@/store/modules/global/global-actions';
  import { computed, ref, watch } from 'vue';
  import { DialogResult } from '@/store/modules/global/global-types';
  import BaseDialog from '../BaseDialog.vue';
  import LoadingOverlay from '../LoadingOverlay.vue';

  const dialogRef = ref<InstanceType<typeof BaseDialog>>();

  const loading = ref(false);

  const dialog = computed(() => coreState.dialog);

  const confirmBtn = computed(() => ({
    text: dialog.value?.confirm?.text || 'generic.confirm',
    className: dialog.value?.confirm?.class || 'dnl:bg-blue-700 dnl:text-white',
    showLoading: true,
  }));

  const discardBtn = computed(() => ({
    text: dialog.value?.discard?.text || 'generic.cancel',
    className: dialog.value?.discard?.class || 'dnl:bg-slate-500 dnl:text-white',
  }));

  const btnClass = 'dnl:p-3 dnl:font-bold dnl:text-sm dnl:rounded dnl:grow';

  async function discard() {
    await dialog.value?.onResolve(DialogResult.Reject);
    clearDialog();
  }

  async function confirm() {
    try {
      if (dialog.value?.loaderOnResolve) {
        loading.value = true;
      }
      await dialog.value?.onResolve(DialogResult.Accept);
      clearDialog();
    } catch {
      loading.value = false;
    }
  }

  async function close() {
    await dialog.value?.onResolve(DialogResult.Close);
    clearDialog();
  }

  watch(
    () => dialog.value,
    () => {
      loading.value = false;

      if (dialog.value) {
        dialogRef.value?.show();
      } else {
        dialogRef.value?.close();
      }
    },
  );
</script>
